import React, { useState, useEffect } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface WatchdogGuardProps {
  isLoading: boolean;
  onRetry: () => void;
  children: React.ReactNode;
  fallbackText?: string;
  timeoutMs?: number;
}

export const WatchdogGuard: React.FC<WatchdogGuardProps> = ({ 
  isLoading, 
  onRetry, 
  children, 
  fallbackText,
  timeoutMs = 3000 
}) => {
  const { t } = useTranslation();
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (isLoading) {
      timer = setTimeout(() => {
        setShowFallback(true);
      }, timeoutMs);
    } else {
      setShowFallback(false);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isLoading, timeoutMs]);

  if (isLoading && showFallback) {
    return (
      <div className="w-full flex flex-col items-center justify-center p-8 space-y-6 bg-slate-50 dark:bg-slate-900 rounded-3xl border border-black/[0.06] dark:border-white/[0.06] animate-in fade-in duration-300">
        <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center animate-pulse">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
        <div className="text-center max-w-md">
          <p className="text-xs font-normal text-ink uppercase font-mono tracking-[0.14em] leading-relaxed">
            {t('watchdog.taking_long', 'O carregamento está demorando... pode ser uma instabilidade na rede, tente forçar o carregamento.')}
          </p>
        </div>
        <button 
          onClick={() => {
            setShowFallback(false);
            onRetry();
          }}
          className="flex items-center gap-2 px-6 py-3 bg-surface dark:bg-slate-950 border border-black/[0.06] dark:border-white/[0.06] rounded-2xl font-medium text-[10px] hover:border-primary transition-colors text-slate-600 dark:text-slate-300"
        >
          <RefreshCw size={14} />
          {t('watchdog.retry', 'Tentar Novamente')}
        </button>
      </div>
    );
  }

  return <>{children}</>;
};
